<template>
  <!-- Provide default properties for all Iconoir icons -->
  <IconoirProvider
    :icon-props="{ width: '20px', height: '20px' }"
  >
    <template v-if="userStore.isLoading">
      <Transition>
        <LoadingScreen v-if="userStore.isLoading"></LoadingScreen>
      </Transition>
    </template>
  
    <template v-else>
      <RouterView />
    </template>
  </IconoirProvider>

  <!--
    The main App component doesn't do much, so let's have it handle the toasts
  -->
  <div id="toast-container">
    <!--
      This transition group ensures (together with the classes defined below)
      that the toasts "fly in" and "fly out".
      -->
    <TransitionGroup name="toast">
      <div
        v-for="toast of toastStore.toasts" :key="toast.timestamp" :class="`toast ${toast.type}`"
        @click="toastStore.dismissToast(toast)"
      >
        {{ toast.message }}
        <div class="toast-countdown" :style="`animation-duration: ${toast.showForSeconds}s;`"></div>
      </div>
    </TransitionGroup>
  </div>

  <div id="banner-container">
    <!-- If the user has never agreed to notifications (permission "default"), ask them to do so. -->
    <!-- Do not show this if they explicitly rejected ("denied"). -->
    <div v-for="banner, i in bannerStore.banners" :key="i" :class="{ banner: true, [banner.type]: true }">
      <div class="message">
        {{ banner.message }}
      </div>
      <div class="spacer"></div>
      <InputButton
        v-for="action in banner.actions"
        :key="action"
        display="primary"
        @click="doBannerAction(banner, action)"
      >
        {{ action }}
      </InputButton>
      <InputButton display="secondary" @click="bannerStore.dismiss(banner)">
        Dismiss
      </InputButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, watch } from "vue"
import { RouterView } from "vue-router"
import { useToastStore } from "./pinia/toast-store"
import LoadingScreen from "./views/LoadingScreen.vue"
import { useUserStore } from "./pinia/user-store"
import InputButton from "./components/InputButton.vue"
import { useBannerStore, type Banner } from "./pinia/banner-store"
import { isMobile } from "./util/device-checks"
import { IconoirProvider } from "@iconoir/vue"

const toastStore = useToastStore()
const bannerStore = useBannerStore()
const userStore = useUserStore()

watch(toastStore.toasts, value => {
  for (const t of value) {
    if (t.timeout !== undefined) {
      continue
    }
    // Set timeout to make the toast disappear after the provided time
    t.timeout = setTimeout(() => {
      const idx = toastStore.toasts.indexOf(t)
      if (idx > -1) {
        toastStore.toasts.splice(idx, 1)
      }
    }, 1000 * t.showForSeconds)
  }
})

onMounted(() => {
  document.body.classList.toggle("mobile", isMobile())
})

function doBannerAction (banner: Banner, action: string) {
  if (banner.actionCallback !== undefined) {
    banner.actionCallback(action)
  }

  bannerStore.dismiss(banner)
}
</script>

<style scoped>
div#banner-container {
  position: fixed;
  bottom: env(safe-area-inset-bottom);
  right: 0;
  left: 0;
  padding: 0px 20px 20px 20px;
  z-index: 10;
  overflow: hidden;
  pointer-events: none;

  .banner {
    pointer-events: all;
    width: 100%;
    display: flex;
    gap: 10px;
    align-items: center;
    margin-top: 20px;
    padding: 10px 20px;
    border-radius: 5px;

    .spacer {
      flex-grow: 1;
    }

    &.info {
      background-color: var(--color-background-mute);
      border: 1px solid var(--color-border);
      color: var(--color-text);
    }
  }
}

div#toast-container {
  position: fixed;
  top: env(safe-area-inset-top);
  right: 0;
  pointer-events: none;
  overflow: hidden;
  z-index: 10;
  padding-top: 50px;
  padding-right: 20px;

  .toast {
    pointer-events: all;
    background-color: var(--color-background);
    border: 1px solid var(--color-border);
    border-radius: 5px;
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.175);
    position: relative;
    text-overflow: ellipsis;
    overflow: hidden;
    display: flex;
    text-align: center;
    align-items: center;
    cursor: default;

    padding: 10px;
    width: 250px;
    height: 100px;
    font-size: 0.8em;
    line-height: 1em;

    &:not(:first-child) {
      margin-top: 10px;
    }

    &.info {
      background-color: var(--color-background-info);
      color: var(--color-info);
      .toast-countdown { background-color: var(--color-info); }
    }
    &.success {
      background-color: var(--color-background-success);
      color: var(--color-success);
      .toast-countdown { background-color: var(--color-success); }
    }
    &.danger {
      background-color: var(--color-background-danger);
      color: var(--color-danger);
      .toast-countdown { background-color: var(--color-danger); }
    }

    .toast-countdown {
      position: absolute;
      left: 0;
      width: 100%;
      height: 2px;
      bottom: 0;
      animation-name: count-down;
      animation-timing-function: linear;
      /* Keep the last frame (width = 0%) after the animation ends */
      animation-fill-mode: forwards;
    }
  }

  .toast-enter-active,
  .toast-leave-active {
    transition: all 0.5s ease;
  }

  .toast-enter-from,
  .toast-leave-to {
    opacity: 0;
    transform: translateX(30px);
  }
}

.v-enter-active,
.v-leave-active {
  transition: opacity 0.5s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}

/* Toast countdown animation; depends on custom animation */
@keyframes count-down {
  100% {
    width: 0%;
  }
  0% {
    width: 100%;
  }
}

header {
  line-height: 1.5;
  max-height: 100vh;
}

.logo {
  display: block;
  margin: 0 auto 2rem;
}

@media (min-width: 1024px) {
  header {
    display: flex;
    place-items: center;
    padding-right: calc(var(--section-gap) / 2);
  }

  .logo {
    margin: 0 2rem 0 0;
  }

  header .wrapper {
    display: flex;
    place-items: flex-start;
    flex-wrap: wrap;
  }
}
</style>
